<template>
  <form v-if="isLoaded" @submit.prevent="submitForm" novalidate>
    <div class="row">
      <div class="col-lg-3 col-md-4 col-sm-6 col-12">
        <Datepicker
            v-model="formData.terminationDate"
            :required="true"
            label="Enddatum"
            position="down"
            :validation="v$.terminationDate"
            :messages="v$.terminationDate.required.$message"/>
      </div>

      <div class="col-lg-3 col-md-4 col-sm-6 col-12">
        <BaseInput v-model="formData.contactEmail"
                   type="email"
                   label="LWL Kontaktmail"
                   :required=true
                   :validation="v$.contactEmail"
                   :messages="v$.contactEmail.required.$invalid ? v$.contactEmail.required.$message : v$.contactEmail.email.$message">
        </BaseInput>
      </div>
      <div class="col-lg-6 col-md-4 col-sm-6 col-12">
        <SelectVue v-model="formData.reasonForTermination"
                   :options="reasonForTerminationDropdown"
                   :is-clear="false"
                   label="Grund der Beendigung"
                   :validation="v$.reasonForTermination"
                   :messages="v$.reasonForTermination.required.$message"/>
      </div>
    </div>

    <div class="d-flex justify-content-center w-100 mt-3">
      <button class="btn btn-primary" type="submit">Versenden</button>
    </div>
  </form>
</template>

<script setup>
import assistedLivingCoachingService from '@/api/Unemployed/AssistedLivingCoaching';
import BaseInput from '@/components/base/BaseInput.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import SelectVue from '@/components/SelectVue.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import { emailRule, requiredRule } from '@/shared/validators/validationRules';
import { piniaEnumsStore } from '@/store/piniaEnumsStore';
import useVuelidate from '@vuelidate/core';
import { computed, defineEmits, defineProps, onMounted, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

const piniaEnumsState              = piniaEnumsStore();
const getEnums                     = computed(() => piniaEnumsState.enums);
const { t }                        = useI18n();
const isLoaded                     = ref(false);
const isSubmitting                 = ref(false);
const coachingId                   = ref(null);
const reasonForTerminationDropdown = ref([]);
const emit                         = defineEmits([ 'update:modelValue', 'update:submitForm' ]);

const props = defineProps({
  assistedLivingCoachingId: {
    type: Number,
    required: true,
  },
  submitForm: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const assistedLivingCoachingId = toRefs(props)[ 'assistedLivingCoachingId' ];

const formData = ref({
  terminationDate: '',
  contactEmail: '',
  reasonForTermination: '',
});

const formRules = ref({
  terminationDate: requiredRule(t),
  contactEmail: emailRule(t),
  reasonForTermination: requiredRule(t),
});

const computedRules = computed(() => formRules.value);
const v$            = useVuelidate(computedRules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    await assistedLivingCoachingService.assistedLivingCoachingTermination(formData.value, coachingId.value );

    emit('update:modelValue', false);
    emit('update:submitForm', !props.submitForm);

    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getAssistedLivingCoachingData(assistedLivingCoachingId) {
  try {
    CloseToast();
    const { data }     = await assistedLivingCoachingService.getAssistedLivingCoaching(+assistedLivingCoachingId);
    const coachingData = data.data;
    coachingId.value   = coachingData.id;

    if (coachingData.terminationDate !== null && coachingData.terminationDate !== '') {
      formData.value.terminationDate      = coachingData.terminationDate;
      formData.value.contactEmail         = coachingData.terminationContactEmail;
      formData.value.reasonForTermination = coachingData.reasonForTermination;
    } else {
      formData.value.terminationDate = coachingData.endDate;
    }

  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  await getAssistedLivingCoachingData(assistedLivingCoachingId.value);
  reasonForTerminationDropdown.value = getEnums?.value?.reasonsForTermination || [];
  isLoaded.value                     = true;
});
</script>
<style scoped>

</style>